import { Eye, CheckCircle, XCircle } from "lucide-react";
import { MobileCardsSkeleton } from "@/components/shared/mobile-cards-skeleton";
import type { RedemptionItem } from "../modals/types";

interface RedemptionMobileCardsProps {
  paginatedItems: RedemptionItem[];
  filteredItems: RedemptionItem[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onView: (item: RedemptionItem) => void;
  onMarkProcessed: (item: RedemptionItem) => void;
  canMarkProcessed: (item: RedemptionItem) => boolean;
  onCancelRequest?: (item: RedemptionItem) => void;
}

export function RedemptionMobileCards({
  paginatedItems,
  filteredItems,
  loading,
  onView,
  onMarkProcessed,
  canMarkProcessed,
  onCancelRequest,
}: RedemptionMobileCardsProps) {
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/40 dark:border-yellow-800/50 dark:text-yellow-300";
      case "APPROVED":
        return "bg-emerald-100 border border-emerald-200 text-emerald-800 dark:bg-emerald-900/40 dark:border-emerald-800/50 dark:text-emerald-300";
      case "REJECTED":
        return "bg-rose-100 border border-rose-200 text-rose-800 dark:bg-rose-900/40 dark:border-rose-800/50 dark:text-rose-300";
      default:
        return "bg-slate-100 border border-slate-200 text-slate-800 dark:bg-slate-800 dark:border-slate-700/50 dark:text-slate-300";
    }
  };

  const getProcessingStatusColor = (status: string | undefined) => {
    switch (status?.toUpperCase()) {
      case "NOT_PROCESSED":
        return "bg-zinc-100 border border-zinc-200 text-zinc-800 dark:bg-zinc-900/40 dark:border-zinc-800/50 dark:text-zinc-300";
      case "PARTIALLY_PROCESSED":
        return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
      case "PROCESSED":
        return "bg-blue-100 border border-blue-200 text-blue-800 dark:bg-blue-900/40 dark:border-blue-800/50 dark:text-blue-300";
      case "CANCELLED":
        return "bg-rose-100 border border-rose-200 text-rose-800 dark:bg-rose-900/40 dark:border-rose-800/50 dark:text-rose-300";
      default:
        return "bg-slate-100 border border-slate-200 text-slate-800 dark:bg-slate-800 dark:border-slate-700/50 dark:text-slate-300";
    }
  };

  return (
    <div
      className="border rounded-lg overflow-hidden bg-card border-border transition-colors"
    >
      <div className="space-y-3 p-4">
        {loading ? (
          <MobileCardsSkeleton count={6} showHeader={false} />
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No requests found
          </div>
        ) : (
          paginatedItems.map((item) => (
            <div
              key={item.id}
              className="p-4 rounded-lg border bg-card border-border"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-semibold text-sm">
                    {item.requested_by_name}
                  </p>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(
                      item.status
                    )}`}
                  >
                    {item.status_display || item.status}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${getProcessingStatusColor(
                      item.processing_status
                    )}`}
                  >
                    {item.processing_status_display || item.processing_status?.replace(/_/g, ' ') || "Not Processed"}
                  </span>
                </div>
              </div>
              <div className="space-y-1 mb-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">For:</span>
                  <span className="font-medium">{item.requested_for_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Points:</span>
                  <span className="font-medium">
                    {item.total_points.toLocaleString()} pts
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium">
                    {new Date(item.date_requested).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onView(item)}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <Eye className="h-4 w-4" />
                  View
                </button>
                {canMarkProcessed(item) && (
                  <button
                    onClick={() => onMarkProcessed(item)}
                    className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Process
                  </button>
                )}
                {onCancelRequest &&
                  item.status === "APPROVED" &&
                  item.processing_status !== "CANCELLED" &&
                  item.processing_status !== "PROCESSED" && (
                  <button
                    onClick={() => onCancelRequest(item)}
                    className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1 bg-red-500 hover:bg-red-600 text-white"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
